"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 10;
      const floaters = heroRef.current.querySelectorAll<HTMLElement>(".parallax-float");
      floaters.forEach((el, i) => {
        const factor = (i + 1) * 0.3;
        el.style.transform = `translate(${x * factor}px, ${y * factor}px)`;
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1627309366653-2dedc084cdf1"
          alt="現代倉儲與日用品貿易背景圖，展示整齊排列的貨架與商品"
          fill
          className="object-cover opacity-30"
          priority />
        
        <div className="absolute inset-0 bg-gradient-to-r from-brand-bg via-brand-bg/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-transparent to-brand-bg/60" />
      </div>

      {/* Ambient glows */}
      <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full blur-[120px] opacity-20 pointer-events-none"
      style={{ background: "radial-gradient(circle, #C41E3A 0%, transparent 70%)" }} />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-[100px] opacity-15 pointer-events-none"
      style={{ background: "radial-gradient(circle, #D4A843 0%, transparent 70%)" }} />

      {/* Content */}
      <div className="container-main relative z-10 pt-28 pb-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 glass-card-gold px-4 py-2 rounded-full mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 blink" />
            <span className="text-xs text-gold-DEFAULT tracking-widest uppercase font-medium">
              香港 · 上海 — 日用品貿易專家
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-5xl md:text-7xl font-bold leading-[1.1] text-white mb-6">
            信悅日用品<br />
            <span className="text-gradient-gold">有限公司</span>
          </h1>

          <p className="text-lg md:text-xl text-white/60 leading-relaxed mb-4 max-w-xl">
            專業日用品貿易，連接上海製造與香港市場
          </p>
          <p className="text-sm text-white/40 leading-relaxed mb-10 max-w-lg">
            XinyueDailyNecessities Co., Ltd. — Your trusted partner for quality daily necessities, bridging Shanghai and Hong Kong.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4">
            <Link href="/products" className="btn-primary px-8 py-3.5 rounded-lg text-white font-semibold text-sm">
              瀏覽產品目錄
            </Link>
            <a
              href="https://wa.me/85294927816"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn flex items-center gap-2 px-8 py-3.5 rounded-lg text-white font-semibold text-sm">
              
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              即時 WhatsApp 查詢
            </a>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap gap-8 mt-16 pt-10 border-t border-white/[0.08]">
            {[
            { num: "500+", label: "產品種類" },
            { num: "2", label: "城市據點" },
            { num: "100+", label: "合作客戶" },
            { num: "5年+", label: "行業經驗" }].
            map((s) =>
            <div key={s.label}>
                <p className="stat-number">{s.num}</p>
                <p className="text-xs text-white/40 mt-1">{s.label}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating product cards */}
      <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 z-10 space-y-4 parallax-float">
        {[
        { label: "清潔用品", img: "https://images.unsplash.com/photo-1649073005971-37babef31983", alt: "清潔用品展示圖，瓶裝清潔劑整齊排列" },
        { label: "個人護理", img: "https://images.unsplash.com/photo-1646579360571-de5ecf3af648", alt: "個人護理產品展示，護膚品與洗護用品" },
        { label: "廚房用品", img: "https://images.unsplash.com/photo-1651240257146-ef492ffcce8f", alt: "廚房日用品，鍋具與廚具陳列" }].
        map((card, i) =>
        <div
          key={card.label}
          className={`glass-card rounded-xl overflow-hidden w-44 ${i === 1 ? "ml-8" : ""}`}
          style={{ animationDelay: `${i * 0.5}s` }}>
          
            <div className="h-28 overflow-hidden">
              <AppImage
              src={card.img}
              alt={card.alt}
              width={176}
              height={112}
              className="w-full h-full object-cover product-img" />
            
            </div>
            <div className="p-2.5">
              <p className="text-xs text-white font-medium">{card.label}</p>
              <p className="text-[10px] text-white/40 mt-0.5">查看產品 →</p>
            </div>
          </div>
        )}
      </div>
    </section>);

}